import React, { useState, createContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import {
  ApiResponse,
  ManualSyncResponse,
  SyncCalendar,
  ValidateApiResponse,
  ValidateiOS
} from '../models/SyncToCalendar';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';

const syncToCalendarDefaut: any = {};
const manualSyncCalendarDefaut: any = {};
const validateiOSDataDefaut: any = {};

const SyncToCalendarContext = createContext({
  syncToCalData: syncToCalendarDefaut as ApiResponse,
  syncToCalendar: (syncToCal: SyncCalendar) => {},
  setSyncCalendarLoading: (boolean) => {},
  syncCalendarLoading: true,
  manualSyningData: (profileData) => {},
  manualSyncData: manualSyncCalendarDefaut as ManualSyncResponse,
  manualSyncLoading: true,
  validateiOSEmail: (iOSData: ValidateiOS) => {},
  validateiOSData: validateiOSDataDefaut as ValidateApiResponse,
  validateiOSEmailLoading: true
});

export const SyncToCalendarProvider = (props) => {
  const { user } = useAuth();
  const { setErrorCode } = useApiError();
  const [syncCalendarLoading, setSyncCalendarLoading] = useState(false);
  const [syncToCalData, setSyncToCalData] =
    useState<ApiResponse>(syncToCalendarDefaut);
  const [manualSyncData, setManualSyncData] = useState<ManualSyncResponse>(
    manualSyncCalendarDefaut
  );
  const [manualSyncLoading, setManualSyncLoading] = useState(false);
  const [validateiOSData, setValidateiOSData] = useState<ValidateApiResponse>(
    validateiOSDataDefaut
  );
  const [validateiOSEmailLoading, setValidateiOSEmailLoading] = useState(false);

  const syncToCalendar = async (syncToCal: SyncCalendar) => {
    if (!user || !syncToCal) {
      return;
    }
    try {
      setSyncCalendarLoading(true);
      const requestObject = {
        body: {
          operation: syncToCal.operation,
          emailId: syncToCal.emailId,
          accountType: syncToCal.accountType,
          token: syncToCal.token,
          screenName: syncToCal.screenName,
          presetName: syncToCal.presetName,
          [syncToCal.emailId]: syncToCal.emailObject
        }
      };
      const syncToCalData = await API.post(
        'crewlife',
        '/calendarapi/syncaccounts',
        requestObject
      );
      setSyncToCalData(syncToCalData);
      setSyncCalendarLoading(false);
    } catch (e: any) {
      setSyncCalendarLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setSyncCalendarLoading(false);
    }
  };

  const manualSyningData = async (profileData: string) => {
    if (!user || !profileData) {
      return;
    }
    try {
      setManualSyncLoading(true);
      const requestObject = {
        body: {
          eventType: [{ EMPLOYEE_ID: profileData }]
        }
      };
      const manualSyncData = await API.post(
        'crewlife',
        '/calendarapi/calendarsync',
        requestObject
      );
      setManualSyncData(manualSyncData);
      setManualSyncLoading(false);
    } catch (e: any) {
      setManualSyncLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setManualSyncLoading(false);
    }
  };

  const validateiOSEmail = async (iOSData: ValidateiOS) => {
    if (!user || !iOSData) {
      return;
    }
    try {
      setValidateiOSEmailLoading(true);
      const requestObject = {
        body: {
          operation: iOSData.operation,
          emailId: iOSData.emailId,
          token: iOSData.token,
          accountType: iOSData.accountType
        }
      };
      const validateiOSData = await API.post(
        'crewlife',
        '/calendarapi/syncaccounts',
        requestObject
      );
      setValidateiOSData(validateiOSData);
      setValidateiOSEmailLoading(false);
      return validateiOSData;
    } catch (e: any) {
      setValidateiOSEmailLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setValidateiOSEmailLoading(false);
    }
  };

  return (
    <SyncToCalendarContext.Provider
      value={{
        syncToCalData,
        syncToCalendar,
        setSyncCalendarLoading,
        syncCalendarLoading,
        manualSyningData,
        manualSyncData,
        manualSyncLoading,
        validateiOSEmail,
        validateiOSData,
        validateiOSEmailLoading
      }}
    >
      {props.children}
    </SyncToCalendarContext.Provider>
  );
};

export const useSyncToCalendarProvider = () => {
  return React.useContext(SyncToCalendarContext);
};
